<script setup lang="ts">
  import { reactive, ref, withDefaults, watch } from 'vue';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import { OrderType } from '@/enums/OrderEnums';

  interface Props {
    value?: {
      validFrom: string | null;
      validUntil: string | null;
      maxUses: number | null;
      maxUsesPerCustomer: number | null;
      minOrderAmount: number | null;
      maxOrderAmount: number | null;
      allowedPaymentMethods: number[];
      allowedOrderTypes: string[] | null;
    };
    currencySymbol?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    value: () => ({
      validFrom: null,
      validUntil: null,
      maxUses: null,
      maxUsesPerCustomer: null,
      minOrderAmount: null,
      maxOrderAmount: null,
      allowedPaymentMethods: [],
      allowedOrderTypes: null,
    }),
    currencySymbol: '$',
  });
  const emit = defineEmits(['input']);

  // Local copy of data
  const localData = reactive({
    validFrom: props.value.validFrom,
    validUntil: props.value.validUntil,
    maxUses: props.value.maxUses,
    maxUsesPerCustomer: props.value.maxUsesPerCustomer,
    minOrderAmount: props.value.minOrderAmount,
    maxOrderAmount: props.value.maxOrderAmount,
    allowedPaymentMethods: props.value.allowedPaymentMethods || [],
    allowedOrderTypes: props.value.allowedOrderTypes || [],
  });

  // Watch prop changes and update local data
  watch(
    () => props.value,
    (newValue) => {
      localData.validFrom = newValue.validFrom;
      localData.validUntil = newValue.validUntil;
      localData.maxUses = newValue.maxUses;
      localData.maxUsesPerCustomer = newValue.maxUsesPerCustomer;
      localData.minOrderAmount = newValue.minOrderAmount;
      localData.maxOrderAmount = newValue.maxOrderAmount;
      localData.allowedPaymentMethods = newValue.allowedPaymentMethods || [];
      localData.allowedOrderTypes = newValue.allowedOrderTypes || [];
    },
    { deep: true }
  );

  // Watch local data changes and emit to parent
  watch(
    localData,
    (newValue) => {
      emit('input', {
        ...newValue,
        maxUses: newValue.maxUses || null,
        maxUsesPerCustomer: newValue.maxUsesPerCustomer || null,
        minOrderAmount: newValue.minOrderAmount || null,
        maxOrderAmount: newValue.maxOrderAmount || null,
        validFrom: newValue.validFrom || null,
        validUntil: newValue.validUntil || null,
        allowedPaymentMethods: newValue.allowedPaymentMethods?.length
          ? newValue.allowedPaymentMethods
          : null,
        allowedOrderTypes: newValue.allowedOrderTypes?.length
          ? newValue.allowedOrderTypes
          : null,
      });
    },
    { deep: true }
  );

  // Validation rules
  const dateRules = {
    validFrom: [
      (v: string | null) => {
        if (!v || !localData.validUntil) return true;
        return (
          new Date(v) <= new Date(localData.validUntil) ||
          'La fecha de inicio debe ser anterior a la fecha de fin'
        );
      },
    ],
    validUntil: [
      (v: string | null) => {
        if (!v || !localData.validFrom) return true;
        return (
          new Date(v) >= new Date(localData.validFrom) ||
          'La fecha de fin debe ser posterior a la fecha de inicio'
        );
      },
    ],
  };

  const numberRules = [
    (v: number | null) => !v || v > 0 || 'El valor debe ser mayor a 0',
  ];

  const orderAmountRules = {
    min: [
      (v: number | null) => {
        if (!v || !localData.maxOrderAmount) return true;
        return (
          v <= localData.maxOrderAmount ||
          'El monto mínimo debe ser menor al máximo'
        );
      },
    ],
    max: [
      (v: number | null) => {
        if (!v || !localData.minOrderAmount) return true;
        return (
          v >= localData.minOrderAmount ||
          'El monto máximo debe ser mayor al mínimo'
        );
      },
    ],
  };

  // Payment methods
  const paymentMethods = [
    { value: PaymentMethod.Cash, text: 'Efectivo', icon: 'mdi-cash' },
    { value: PaymentMethod.Card, text: 'Tarjeta', icon: 'mdi-credit-card' },
    { value: PaymentMethod.Online, text: 'Online / MercadoPago', icon: 'mdi-web' },
    { value: PaymentMethod.QR, text: 'QR', icon: 'mdi-qrcode' },
  ];

  // Order types
  const orderTypes = [
    { value: OrderType.Seat, text: 'En mesa', icon: 'mdi-table-chair' },
    { value: OrderType.Away, text: 'Para llevar', icon: 'mdi-shopping' },
    { value: OrderType.Delivery, text: 'Delivery', icon: 'mdi-moped' },
  ];

  // Helper to get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  // Date menu states
  const dateFromMenu = ref(false);
  const dateUntilMenu = ref(false);
</script>

<template>
  <div class="v2-modal-coupon-restrictions">
    <!-- Date restrictions -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-calendar</v-icon>
        Vigencia
      </h4>

      <v-row>
        <v-col cols="12" md="6">
          <v-menu
            v-model="dateFromMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData.validFrom"
                label="Válido desde (opcional)"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="dateRules.validFrom"
                outlined
                dense
                clearable
              />
            </template>
            <v-date-picker
              v-model="localData.validFrom"
              :min="today"
              :locale="$i18n.locale"
              @input="dateFromMenu = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" md="6">
          <v-menu
            v-model="dateUntilMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData.validUntil"
                label="Válido hasta (opcional)"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="dateRules.validUntil"
                outlined
                dense
                clearable
              />
            </template>
            <v-date-picker
              v-model="localData.validUntil"
              :min="localData.validFrom || today"
              :locale="$i18n.locale"
              @input="dateUntilMenu = false"
            />
          </v-menu>
        </v-col>
      </v-row>
    </div>

    <v-divider class="my-4" />

    <!-- Usage limits -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-counter</v-icon>
        Límites de uso
      </h4>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="localData.maxUses"
            label="Cantidad de cupones disponibles (opcional)"
            type="number"
            :rules="numberRules"
            outlined
            dense
            hint="Dejar vacío para uso ilimitado"
            persistent-hint
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="localData.maxUsesPerCustomer"
            label="Usos por cliente (opcional)"
            type="number"
            :rules="numberRules"
            outlined
            dense
            hint="Límite por cliente"
            persistent-hint
          />
        </v-col>
      </v-row>
    </div>

    <v-divider class="my-4" />

    <!-- Order amount restrictions -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-cash-check</v-icon>
        Montos de pedido
      </h4>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="localData.minOrderAmount"
            label="Monto mínimo (opcional)"
            :prefix="currencySymbol"
            type="number"
            :rules="orderAmountRules.min"
            outlined
            dense
            hint="Pedido mínimo requerido"
            persistent-hint
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="localData.maxOrderAmount"
            label="Monto máximo (opcional)"
            :prefix="currencySymbol"
            type="number"
            :rules="orderAmountRules.max"
            outlined
            dense
            hint="Pedido máximo permitido"
            persistent-hint
          />
        </v-col>
      </v-row>
    </div>

    <v-divider class="my-4" />

    <!-- Payment methods -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-credit-card</v-icon>
        Métodos de pago permitidos
      </h4>

      <div class="checkbox-group">
        <v-checkbox
          v-for="method in paymentMethods"
          :key="method.value"
          v-model="localData.allowedPaymentMethods"
          :label="method.text"
          :value="method.value"
          hide-details
          class="mb-2"
        >
          <template v-slot:prepend>
            <v-icon :small="true" class="mr-2">{{ method.icon }}</v-icon>
          </template>
        </v-checkbox>
      </div>

      <p class="text-caption text--secondary mt-2">
        Si no selecciona ninguno, el cupón será válido para todos los métodos
      </p>

      <v-alert
        v-if="!localData.allowedPaymentMethods?.length"
        type="info"
        outlined
        dense
      >
        El cupón será válido para todos los métodos
      </v-alert>
    </div>

    <v-divider class="my-4" />

    <!-- Order types -->
    <div class="section">
      <h4 class="section-title">
        <v-icon small left>mdi-shopping</v-icon>
        Tipos de pedido permitidos
      </h4>

      <div class="checkbox-group">
        <v-checkbox
          v-for="type in orderTypes"
          :key="type.value"
          v-model="localData.allowedOrderTypes"
          :label="type.text"
          :value="type.value"
          hide-details
          class="mb-2"
        >
          <template v-slot:prepend>
            <v-icon small class="mr-2">{{ type.icon }}</v-icon>
          </template>
        </v-checkbox>
      </div>

      <p class="text-caption text--secondary mt-2">
        Si no selecciona ninguno, el cupón será válido para todos los tipos
      </p>

      <v-alert
        v-if="!localData.allowedOrderTypes?.length"
        type="info"
        outlined
        dense
      >
        El cupón será válido para todos los tipos
      </v-alert>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-coupon-restrictions {
    .section {
      @apply tw-mb-4;

      .section-title {
        @apply tw-text-base tw-font-medium tw-mb-3 tw-flex tw-items-center;
      }
    }

    .checkbox-group {
      @apply tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4;
    }
  }
</style>
